import { useCallback, useEffect } from 'react';
import { useDiagramStore } from '../stores/diagramStore';
import { SEQUENCE } from '../constants';
import {
  computeMultiPushDragUpdates,
  computeMultiReorderDragUpdates,
  computeSpacingUpdatesFromAnchor,
  estimateSequenceMessageFootprint,
  resolveMessageInsertionY,
} from '../utils/sequenceMessageLayout';
import { fragmentBoundaryGapExtra, resolveMessageFragmentId } from '../utils/fragmentLayout';
import type { SequenceMessage } from '../types';

const SEQUENCE_MIN_LABEL_WIDTH = SEQUENCE.MIN_LABEL_WIDTH;
const SEQUENCE_LABEL_SAFE_MARGIN = SEQUENCE.LABEL_SAFE_MARGIN;
const SEQ_VERTICAL_BUFFER = SEQUENCE.MESSAGE_VERTICAL_BUFFER;
const MSG_PADDING = 40;

interface UseSequenceMessageLayoutArgs {
  participants: ReturnType<typeof useDiagramStore.getState>['participants'];
  messages: SequenceMessage[];
  fragments: ReturnType<typeof useDiagramStore.getState>['fragments'];
  selectedItems: string[];
  applySequenceLayoutPatch: ReturnType<typeof useDiagramStore.getState>['applySequenceLayoutPatch'];
  addMessage: ReturnType<typeof useDiagramStore.getState>['addMessage'];
  updateFragment: ReturnType<typeof useDiagramStore.getState>['updateFragment'];
  snapshotBeforeMutation: () => void;
  canMutateStructure: boolean;
  ensureFragmentCascadeEndY: (fragmentId: string, requiredEndY: number) => number;
  dragSelectedMessageIdsRef: React.MutableRefObject<string[]>;
  dragReorderSnapshotRef: React.MutableRefObject<unknown>;
  reorderMessageDragKeyRef: React.MutableRefObject<boolean>;
}

export function useSequenceMessageLayout({
  participants,
  messages,
  fragments,
  selectedItems,
  applySequenceLayoutPatch,
  addMessage,
  updateFragment,
  snapshotBeforeMutation,
  canMutateStructure,
  ensureFragmentCascadeEndY,
  dragSelectedMessageIdsRef,
  dragReorderSnapshotRef,
  reorderMessageDragKeyRef,
}: UseSequenceMessageLayoutArgs) {
  const getSequenceMessageFootprint = useCallback((label: string, sourceId: string, targetId: string) => {
    return estimateSequenceMessageFootprint(
      participants,
      label,
      sourceId,
      targetId,
      { safeMargin: SEQUENCE_LABEL_SAFE_MARGIN, minLabelWidth: SEQUENCE_MIN_LABEL_WIDTH },
    );
  }, [participants]);

  const getSequenceMessageGap = useCallback((
    upper: { label: string; sourceId: string; targetId: string; fragmentId?: string },
    lower: { label: string; sourceId: string; targetId: string; fragmentId?: string },
  ) => {
    const upperFootprint = getSequenceMessageFootprint(upper.label, upper.sourceId, upper.targetId);
    const lowerFootprint = getSequenceMessageFootprint(lower.label, lower.sourceId, lower.targetId);
    const extra = fragmentBoundaryGapExtra(upper, lower, fragments);
    return upperFootprint.below + lowerFootprint.above + SEQ_VERTICAL_BUFFER + extra;
  }, [fragments, getSequenceMessageFootprint]);

  const resolveSequenceMessageY = useCallback((
    proposedY: number,
    options?: { excludeMessageId?: string; label?: string; sourceId?: string; targetId?: string },
  ) => {
    const fallbackSourceId = options?.excludeMessageId
      ? messages.find((message) => message.id === options.excludeMessageId)?.sourceId
      : undefined;
    const fallbackTargetId = options?.excludeMessageId
      ? messages.find((message) => message.id === options.excludeMessageId)?.targetId
      : undefined;
    const movingSourceId = options?.sourceId ?? fallbackSourceId ?? '';
    const movingTargetId = options?.targetId ?? fallbackTargetId ?? '';
    return resolveMessageInsertionY(
      messages,
      proposedY,
      {
        label: options?.label ?? '',
        sourceId: movingSourceId,
        targetId: movingTargetId,
      },
      getSequenceMessageGap,
      options?.excludeMessageId,
    );
  }, [getSequenceMessageGap, messages]);

  const applyMessageYUpdates = useCallback((
    updates: Array<{ id: string; y: number }>,
    options?: { recalcMessageFragments?: boolean },
  ) => {
    if (updates.length === 0) return 0;
    applySequenceLayoutPatch({
      messageYUpdates: updates,
      sortMessages: true,
      recalcMessageFragments: options?.recalcMessageFragments ?? true,
    });
    return updates.length;
  }, [applySequenceLayoutPatch]);

  const enforceSequenceMessageSpacing = useCallback((anchorMessageId?: string) => {
    const updates = computeSpacingUpdatesFromAnchor(
      useDiagramStore.getState().messages,
      getSequenceMessageGap,
      anchorMessageId,
    );
    applyMessageYUpdates(updates);
  }, [applyMessageYUpdates, getSequenceMessageGap]);

  const applyMessageDragWithPush = useCallback((messageId: string, proposedY: number) => {
    const selectedMessageIds = dragSelectedMessageIdsRef.current;
    const updates = computeMultiPushDragUpdates(
      useDiagramStore.getState().messages,
      dragReorderSnapshotRef.current as Parameters<typeof computeMultiPushDragUpdates>[1],
      messageId,
      selectedMessageIds,
      proposedY,
      getSequenceMessageGap,
    );
    return applyMessageYUpdates(updates);
  }, [applyMessageYUpdates, dragReorderSnapshotRef, dragSelectedMessageIdsRef, getSequenceMessageGap]);

  const applyMessageDragWithReorder = useCallback((messageId: string, proposedY: number) => {
    const selectedMessageIds = dragSelectedMessageIdsRef.current;
    const updates = computeMultiReorderDragUpdates(
      useDiagramStore.getState().messages,
      dragReorderSnapshotRef.current as Parameters<typeof computeMultiReorderDragUpdates>[1],
      messageId,
      selectedMessageIds,
      proposedY,
    );
    return applyMessageYUpdates(updates, { recalcMessageFragments: false });
  }, [applyMessageYUpdates, dragReorderSnapshotRef, dragSelectedMessageIdsRef]);

  const resolveDraggedMessageIds = useCallback((orderedIds: string[], draggedId: string) => {
    const selectedSet = new Set(selectedItems);
    const selectedMessageIds = orderedIds.filter((messageId) => selectedSet.has(messageId));
    if (!selectedMessageIds.includes(draggedId) || selectedMessageIds.length < 2) {
      return [draggedId];
    }
    return selectedMessageIds;
  }, [selectedItems]);

  const ensureDraggedMessageFragmentExtents = useCallback((messageIds: string[]) => {
    if (messageIds.length === 0) return 0;

    const state = useDiagramStore.getState();
    const messageById = new Map(state.messages.map((message) => [message.id, message]));
    const requiredEndYByFragmentId = new Map<string, number>();

    messageIds.forEach((messageId) => {
      const message = messageById.get(messageId);
      if (!message?.fragmentId) return;

      const footprint = getSequenceMessageFootprint(
        message.label,
        message.sourceId,
        message.targetId,
      );
      const requiredEndY = message.y + footprint.below + SEQUENCE.FRAGMENT_PAD_BOTTOM;
      const previousRequiredEndY = requiredEndYByFragmentId.get(message.fragmentId) ?? -Infinity;
      if (requiredEndY > previousRequiredEndY) {
        requiredEndYByFragmentId.set(message.fragmentId, requiredEndY);
      }
    });

    let updatedFragments = 0;
    requiredEndYByFragmentId.forEach((requiredEndY, fragmentId) => {
      updatedFragments += ensureFragmentCascadeEndY(fragmentId, requiredEndY);
    });
    return updatedFragments;
  }, [ensureFragmentCascadeEndY, getSequenceMessageFootprint]);

  const finalizeDraggedMessages = useCallback((messageIds: string[], fallbackAnchorId: string) => {
    const state = useDiagramStore.getState();
    const messageById = new Map(state.messages.map((message) => [message.id, message]));
    const messageFragmentUpdates: Array<{ id: string; fragmentId?: string }> = [];

    messageIds.forEach((messageId) => {
      const message = messageById.get(messageId);
      if (!message) return;

      const nextFragmentId = resolveMessageFragmentId(message, state.fragments);
      if (nextFragmentId !== message.fragmentId) {
        messageFragmentUpdates.push({ id: messageId, fragmentId: nextFragmentId });
      }
    });

    if (messageFragmentUpdates.length > 0) {
      applySequenceLayoutPatch({
        messageFragmentUpdates,
        recalcMessageFragments: true,
      });
    }

    const spacingAnchorId = messageIds[0] ?? fallbackAnchorId;
    if (spacingAnchorId) {
      enforceSequenceMessageSpacing(spacingAnchorId);
    }
  }, [applySequenceLayoutPatch, enforceSequenceMessageSpacing]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Shift') reorderMessageDragKeyRef.current = true;
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') reorderMessageDragKeyRef.current = false;
    };
    const onBlur = () => { reorderMessageDragKeyRef.current = false; };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, [reorderMessageDragKeyRef]);

  const addSequenceMessageWithLayout = useCallback((params: {
    sourceId: string;
    targetId: string;
    proposedY: number;
    label?: string;
    type?: SequenceMessage['type'];
  }) => {
    if (!canMutateStructure) return params.proposedY;
    const resolvedY = resolveSequenceMessageY(params.proposedY, {
      label: params.label ?? '',
      sourceId: params.sourceId,
      targetId: params.targetId,
    });
    const containingFragId = resolveMessageFragmentId({
      y: resolvedY,
      sourceId: params.sourceId,
      targetId: params.targetId,
    }, fragments);
    const containingFrag = containingFragId
      ? fragments.find((fragment) => fragment.id === containingFragId)
      : undefined;

    snapshotBeforeMutation();
    addMessage({
      sourceId: params.sourceId,
      targetId: params.targetId,
      y: resolvedY,
      label: params.label ?? '',
      type: params.type ?? 'sync',
      fragmentId: containingFrag?.id,
    });

    let frag = containingFrag;
    while (frag) {
      if (resolvedY + MSG_PADDING > frag.endY) {
        updateFragment(frag.id, { endY: resolvedY + MSG_PADDING });
      }
      const parentId = frag.parentFragmentId;
      frag = parentId ? fragments.find((fragment) => fragment.id === parentId) : undefined;
    }

    enforceSequenceMessageSpacing();
    return resolvedY;
  }, [
    addMessage,
    canMutateStructure,
    enforceSequenceMessageSpacing,
    fragments,
    resolveSequenceMessageY,
    snapshotBeforeMutation,
    updateFragment,
  ]);

  return {
    getSequenceMessageFootprint,
    applyMessageDragWithPush,
    applyMessageDragWithReorder,
    resolveDraggedMessageIds,
    ensureDraggedMessageFragmentExtents,
    finalizeDraggedMessages,
    addSequenceMessageWithLayout,
    enforceSequenceMessageSpacing,
  };
}
